import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import dayjs from 'dayjs';

const DEFAULT_DATE_RANGE = process.env.REACT_APP_DEFAULT_DATE_RANGE || 'yesterday';
const CUSTOM_START_DATE = process.env.REACT_APP_CUSTOM_START_DATE || '01/01/2024';
const CUSTOM_END_DATE = process.env.REACT_APP_CUSTOM_END_DATE || '12/31/2024';

// Helper function to get date range based on selection
const getDefaultDateRange = (rangeType: string) => {
  switch (rangeType) {
    case "today":
      return {
        startDate: dayjs().startOf("day").format("MM/DD/YYYY"),
        endDate: dayjs().endOf("day").format("MM/DD/YYYY"),
      };
    case "yesterday":
      return {
        startDate: dayjs()
          .subtract(1, "day")
          .startOf("day")
          .format("MM/DD/YYYY"),
        endDate: dayjs().subtract(1, "day").endOf("day").format("MM/DD/YYYY"),
      };
    case "last7days":
      return {
        startDate: dayjs()
          .subtract(7, "day")
          .startOf("day")
          .format("MM/DD/YYYY"),
        endDate: dayjs().endOf("day").format("MM/DD/YYYY"),
      };
    case "last30days":
      return {
        startDate: dayjs()
          .subtract(30, "day")
          .startOf("day")
          .format("MM/DD/YYYY"),
        endDate: dayjs().endOf("day").format("MM/DD/YYYY"),
      };
    case "lastMonth":
      return {
        startDate: dayjs()
          .subtract(1, "month")
          .startOf("day")
          .format("MM/DD/YYYY"),
        endDate: dayjs().endOf("day").format("MM/DD/YYYY"),
      };
    case "allData":
      return {
        startDate: null,
        endDate: null,
      };
    case "custom":
      return {
        startDate: CUSTOM_START_DATE,
        endDate: CUSTOM_END_DATE,
      };
    default:
      return {
        startDate: dayjs()
          .subtract(1, "day")
          .startOf("day")
          .format("MM/DD/YYYY"),
        endDate: dayjs().subtract(1, "day").endOf("day").format("MM/DD/YYYY"),
      };
  }
};

export interface FilterState {
  startDate: string | null;
  endDate: string | null;
  filter: string;
  quotedJobcode: string;
  techLdap: string;
  acctgDt: string;
  declineReason: string;
  applianceType: string;
  createSource: string;
  llmChatClassification: string;
  orderNumber: string;
  customerRating: string;
  page: number;
  limit: number;
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc";
}

interface FilterContextType {
  filterStates: Record<number, FilterState>;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  getFilterState: (tab: number) => FilterState;
  updateFilterState: (tab: number, updates: Partial<FilterState>) => void;
  resetFilterState: (tab: number) => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
  const [activeTab, setActiveTab] = useState(0);
  const defaultDates = useMemo(() => getDefaultDateRange(DEFAULT_DATE_RANGE), []);

  const createDefaultFilterState = useCallback((): FilterState => ({
    ...defaultDates,
    filter: "",
    quotedJobcode: "",
    techLdap: "",
    acctgDt: "",
    declineReason: "",
    applianceType: "",
    createSource: "",
    llmChatClassification: "",
    orderNumber: "",
    customerRating: "",
    page: 0,
    limit: 50,
    sortColumn: null,
    sortDirection: "asc",
  }), [defaultDates]);

  // Initialize filter states for all tabs
  const [filterStates, setFilterStates] = useState<Record<number, FilterState>>(() => ({
    0: createDefaultFilterState(),
    1: createDefaultFilterState(),
    2: createDefaultFilterState(),
    3: createDefaultFilterState(),
  }));

  const getFilterState = useCallback((tab: number): FilterState => {
    return filterStates[tab] || createDefaultFilterState();
  }, [filterStates, createDefaultFilterState]);

  const updateFilterState = useCallback((tab: number, updates: Partial<FilterState>) => {
    setFilterStates((prev) => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        ...updates,
      },
    }));
  }, []);

  const resetFilterState = useCallback((tab: number) => {
    setFilterStates((prev) => ({
      ...prev,
      [tab]: createDefaultFilterState(),
    }));
  }, [createDefaultFilterState]);

  return (
    <FilterContext.Provider
      value={{
        filterStates,
        activeTab,
        setActiveTab,
        getFilterState,
        updateFilterState,
        resetFilterState,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};
